import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

// Material UI
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import { makeStyles } from '@mui/styles';

// Components
import CentsLoader from '../../common/centsLoader/CentsLoader';
import AutocompleteDropdown from '../../common/autocompleteDropdown/AutocompleteDropdown';

// APIs
import { registerNewScaleDevice } from '../../../api/admin/scaleDevices/scaleDevices';
import { getAllStores } from '../../../api/admin/stores/stores';

// Utils
import { formatStoreDisplayName } from 'utils/formatters/formatStoreDisplayName';

// Material UI one-time style customization
const useStyles = makeStyles(() => ({
  inputRoot: {
    width: '65%',
  },
  formControl: {
    width: '66.5%',
    paddingRight: '10px',
  },
  autocompleteDropdownInput: {
    margin: '0',
  },
}));

const RegisterScaleDevice = () => {
  const [loading, setLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [formValues, setFormValues] = useState({
    pinNumber: '',
    deviceUuid: '',
    storeId: '',
  });
  const [error, setError] = useState(null);

  const materialClasses = useStyles();
  let history = useHistory();

  useEffect(() => {
    fetchAllStores();
  }, []);

  /**
   * Get a list of all stores in the Cents ecosystem
   */
  const fetchAllStores = async () => {
    try {
      setLoading(true);

      const storeData = await getAllStores();

      const mappedStores = storeData.data.stores.map((store) => ({
        display: formatStoreDisplayName(store.name, store.id),
        value: store.id,
      }));
      setStores(mappedStores);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response.data.error);
    }
  };

  /**
   * Set the input value in our form state
   *
   * @param {String} key
   * @param {String} value
   */
  const handleInputChange = (key, value) => {
    setFormValues({
      ...formValues,
      [key]: value,
    });
  };

  /**
   * Store the incoming store selection in the formValues state object
   *
   * @param {Number} storeId
   */
  const handleStoreChange = (storeId) => {
    setError(null);
    setFormValues({
      ...formValues,
      storeId,
    });
  };

  /**
   * Determine whether to disable the submit button
   */
  const disableButton = () => {
    return !formValues.pinNumber || !formValues.deviceUuid;
  };

  /**
   * Register the scale device within the Cents system
   */
  const saveNewScaleDevice = async () => {
    try {
      setLoading(true);

      const data = {
        pinNumber: formValues.pinNumber,
        deviceUuid: formValues.deviceUuid,
        storeId: formValues.storeId,
      };

      await registerNewScaleDevice(data);

      history.push('/scale-devices');

      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError(error.response.data.error);
    }
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <div className="create-new-user-container">
          <div className="create-new-user-form-container">
            <div className="create-new-user-form">
              <h1>Register New Scale Device</h1>
              <div className="create-new-user-form-row">
                <p className="label">Pin Number</p>
                <TextField
                  variant="outlined"
                  label="Pin Number"
                  onChange={(event) => {
                    handleInputChange('pinNumber', event.target.value);
                  }}
                  className={materialClasses.inputRoot}
                  value={formValues.pinNumber}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Device UUID</p>
                <TextField
                  variant="outlined"
                  label="Device UUID"
                  onChange={(event) => {
                    handleInputChange('deviceUuid', event.target.value);
                  }}
                  className={materialClasses.inputRoot}
                  value={formValues.deviceUuid}
                />
              </div>
              {stores.length > 0 && (
                <div className="create-new-user-form-row">
                  <p className="label">Attach to Store</p>
                  <FormControl variant="outlined" className={materialClasses.formControl}>
                    <AutocompleteDropdown
                      id="select-role"
                      selectedData={formValues.storeId}
                      onChange={(event) => {
                        handleStoreChange(event.value);
                      }}
                      label="Stores"
                      selectData={stores}
                      inputProps={{ className: materialClasses.autocompleteDropdownInput }}
                    />
                  </FormControl>
                </div>
              )}
              <div className="submit-button-container">
                <button
                  className={'submit-button ' + (disableButton() ? 'disabled' : 'active')}
                  onClick={() => {
                    saveNewScaleDevice();
                  }}
                >
                  Register
                </button>
                {error && <p className="error-text">{error}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RegisterScaleDevice;
